import { parseAbiItem, type Address, type PublicClient } from "viem";
import { keelVaultFactoryAbi } from "../abi";
import type { RedKeel } from "../config/networks";
import { useQuery } from "@tanstack/react-query";
import { clienteLogs, clientePublico, logsHistoricos } from "./cliente";

/**
 * LO GANADO DESDE QUE SE ABRIÓ, para una red entera y en UNA consulta.
 *
 * EL PROBLEMA QUE RESUELVE (2026-08-19). `profit0/1` es solo lo que hay apartado AHORA, y
 * `withdrawFees()` lo pone a cero: al cobrar las ganancias, la lista y la portada pasaban de
 * «+12,40 USDG» a «+0,02» y parecía que el dinero ganado se había borrado. Lo ganado es historia y
 * no puede depender de si ya lo has cobrado.
 *
 * SE MIDE CON `PerfFeeTaken`, que es el único evento presente en los TRES caminos por los que se
 * cobra —el cobro a demanda, el cierre de posición y `removeLiquidity`— y además es cruzable: es
 * literalmente lo que se llevó el treasury. La relación con lo cobrado es exacta:
 *
 *     comisión = bruto × bps / 10000     ->     bruto = comisión × 10000 / bps
 *
 * Es la misma cuenta que hace `useGanancias` para la ficha de un vault; aquí se pide para todas las
 * direcciones a la vez, que es lo que permite usarla en una lista sin multiplicar las peticiones.
 *
 * Devuelve un mapa vacío —no ceros— cuando no se puede recorrer el histórico entero: quien lo use
 * tiene que poder distinguir «no lo sé» de «no has ganado nada».
 */
const PERF_FEE = parseAbiItem("event PerfFeeTaken(address indexed treasury, uint256 fee0, uint256 fee1)");

export type Cobrado = { bruto0: bigint; bruto1: bigint; cobros: number };

/**
 * VA APARTE DE LA LISTA, y esa es media solución al «BNB Chain tarda demasiado».
 *
 * Leer el estado de los vaults son milisegundos; recorrer sus eventos, no: BNB Chain no acepta
 * rangos grandes y hoy hacen falta 37 ventanas —y seis más cada día— para llegar desde la factory.
 * Con las dos cosas en la misma consulta, la lista entera esperaba a los eventos y la red parecía
 * rota. Aquí el ganado histórico es su propia consulta: la lista sale enseguida con lo que tiene
 * apartado cada vault, y la cifra se completa sola cuando llegan los eventos.
 */
export function useCobrado(vaults: Address[], red: RedKeel | undefined) {
  return useQuery({
    queryKey: ["cobrado", red?.clave, [...vaults].sort().join(",")],
    enabled: !!red && vaults.length > 0,
    staleTime: 5 * 60_000,
    queryFn: () => cobradoPorVault(clientePublico(red!.idCadena), red!, vaults),
  });
}

export async function cobradoPorVault(
  client: PublicClient,
  red: RedKeel,
  vaults: Address[]
): Promise<Map<string, Cobrado>> {
  const mapa = new Map<string, Cobrado>();
  if (!vaults.length) return mapa;

  const [logs, bps] = await Promise.all([
    logsHistoricos<{ address: string; args: { fee0?: bigint; fee1?: bigint } }>(
      clienteLogs(red),
      { address: vaults, event: PERF_FEE },
      red
    ),
    client
      .readContract({ address: red.factory, abi: keelVaultFactoryAbi, functionName: "perfFeeBps" })
      .then((x) => BigInt(Number(x) || 1000))
      .catch(() => 1000n),
  ]);
  if (!logs) return mapa;

  // El BRUTO, no el neto: es lo que la ficha del vault enseña en «Ganado» y lo único que cuadra con
  // lo que Uniswap dice de la posición, que es donde el dueño va a comprobarlo. El 10% se explica
  // donde de verdad ocurre — en el modal de cobrar.
  const bruto = (comision: bigint) => (bps > 0n ? (comision * 10000n) / bps : 0n);

  for (const l of logs) {
    const k = l.address.toLowerCase();
    const a = mapa.get(k) ?? { bruto0: 0n, bruto1: 0n, cobros: 0 };
    a.bruto0 += bruto(l.args.fee0 ?? 0n);
    a.bruto1 += bruto(l.args.fee1 ?? 0n);
    a.cobros += 1;
    mapa.set(k, a);
  }
  // Un vault sin un solo cobro NO es un vault sin dato: es un cero de verdad, y hay que decirlo
  // como cero para que no se caiga al cálculo antiguo.
  for (const v of vaults) {
    const k = v.toLowerCase();
    if (!mapa.has(k)) mapa.set(k, { bruto0: 0n, bruto1: 0n, cobros: 0 });
  }
  return mapa;
}
